import { useEffect, useState } from "react";
import { apiFetch } from "../../../context/AuthContext";

const useUnreadMessages = ({ BASE_URL, socket, isConnected, messengerConversations }) => {
    const [unreadMessages, setUnreadMessages] = useState(0);

    // If MessengerContext already has conversations loaded (Messages page), use that as the primary source
    useEffect(() => {
        try {
            if (Array.isArray(messengerConversations) && messengerConversations.length > 0) {
                const totalFromContext = messengerConversations.reduce((s, c) => {
                    const u = c.unread ?? c.unreadCount ?? c.unread_count ?? c.unreads ?? 0;
                    return s + (Number(u) || 0);
                }, 0);
                console.log('[DashboardManager] Using MessengerContext unread total:', totalFromContext);
                setUnreadMessages(totalFromContext);
            }
        } catch (err) {
            console.warn('[DashboardManager] Error computing unread from MessengerContext', err);
        }
    }, [messengerConversations]);

    // جلب عدد الرسائل غير المقروءة (مرة واحدة فقط عند التحميل)
    useEffect(() => {
        const fetchUnreadMessages = async () => {
            try {
                if (!BASE_URL) {
                    console.log('[DashboardManager] BASE_URL not set');
                    return;
                }

                console.log('[DashboardManager] Fetching initial unread count from:', `${BASE_URL}/api/private-messages/conversations`);

                const response = await apiFetch(
                    `${BASE_URL}/api/private-messages/conversations`,
                    { method: 'GET' }
                );

                console.log('[DashboardManager] Response status:', response.status, response.ok);

                if (response.ok) {
                    const data = await response.json();
                    console.log('[DashboardManager] Raw API Response:', data);

                    // البيانات قد تأتي كـ array مباشرة أو داخل object
                    let conversations = [];
                    if (Array.isArray(data)) {
                        conversations = data;
                    } else if (data.data && Array.isArray(data.data)) {
                        conversations = data.data;
                    } else if (data.conversations && Array.isArray(data.conversations)) {
                        conversations = data.conversations;
                    }

                    console.log('[DashboardManager] Parsed conversations array length:', conversations.length);

                    // حساب إجمالي الرسائل غير المقروءة
                    let total = 0;
                    conversations.forEach((conv, index) => {
                        // البحث عن unreadCount بطرق مختلفة
                        const unreadCount =
                            conv.unreadCount ??
                            conv.unread_count ??
                            conv.unreadMessagesCount ??
                            conv.unreads ??
                            0;

                        total += Number(unreadCount) || 0;

                        if (index < 3) { // لا نطبع جميعها، فقط أول 3
                            console.log(`[DashboardManager] Conversation: "${conv.recipientName || conv.name || 'Unknown'}" - Unread: ${unreadCount}`);
                        }
                    });

                    console.log('[DashboardManager] ✅ Initial unread messages count:', total);
                    setUnreadMessages(total);
                } else {
                    console.error('[DashboardManager] ❌ API returned error status:', response.status, await response.text());
                }
            } catch (error) {
                console.error('[DashboardManager] ❌ Error fetching unread messages:', error);
            }
        };

        fetchUnreadMessages();
        // No polling - we'll use real-time socket events instead
    }, [BASE_URL]);

    // Real-time socket listener for new messages
    useEffect(() => {
        if (!socket || !isConnected) {
            console.log('[DashboardManager] Socket not ready for unread messages listener');
            return;
        }

        console.log('[DashboardManager] Setting up real-time message listeners');

        // Increment counter when new message is received
        const handleMessageReceived = (data) => {
            console.log('[DashboardManager] 📩 New message received:', data);
            setUnreadMessages(prev => {
                const newCount = prev + 1;
                console.log(`[DashboardManager] Unread count: ${prev} → ${newCount}`);
                return newCount;
            });
        };

        // Decrement counter when messages are marked as read
        const handleMessagesRead = (data) => {
            console.log('[DashboardManager] ✓ Messages marked as read:', data);
            if (data.count) {
                setUnreadMessages(prev => {
                    const newCount = Math.max(0, prev - data.count);
                    console.log(`[DashboardManager] Unread count: ${prev} → ${newCount}`);
                    return newCount;
                });
            }
        };

        socket.on('private_message:received', handleMessageReceived);
        socket.on('private_message:read', handleMessagesRead);

        console.log('[DashboardManager] ✅ Real-time message listeners active');

        return () => {
            socket.off('private_message:received', handleMessageReceived);
            socket.off('private_message:read', handleMessagesRead);
            console.log('[DashboardManager] Cleaned up message listeners');
        };
    }, [socket, isConnected]);

    return { unreadMessages, setUnreadMessages };
};

export default useUnreadMessages;